import moment from "moment";
import { t } from "i18next";

import { getPriceBrackets } from "utils";
import { translation, UNIT_TYPE_LABELS } from "utils/constants/index";
import { getDateString, getFormattedDate } from "utils/methods/index";

export const formatCurrency = (value) => {
  if (value && typeof value === "string" && value.includes("$")) {
    return `${value?.toLocaleString()}`;
  }

  return `$${value?.toLocaleString()}`;
};

export const budgetFormat = (val) => {
  if (typeof val == "string") {
    return val;
  }

  const result = val?.length > 1 ? `${formatCurrency(val?.[0])}-${formatCurrency(val?.[1])}` : formatCurrency(val?.[1]);

  return result;
};

export const getPriceRange = (val) => {
  if (typeof val !== "string") {
    return val;
  }

  const prices = val
    .replace(/[$,]/g, "")
    .split("-")
    .map((price) => parseInt(price, 10));

  return prices;
};

export const getPriceRangeApi = async (propertyId, apiKey, bedroomType) => {
  return getPriceBrackets(`?property_id=${propertyId}&api_key=${encodeURIComponent(apiKey)}&bedroom_type=${bedroomType}`);
};

export const translations = {
  Custom: "custom",
  All: "all",
  "Custom Range": "customRange",
  Any: "any",
};

export function isNull(value) {
  return value === null;
}

export function sortTable(array, key, order = "asc") {
  return array.sort((a, b) => {
    let aValue = a[key];
    let bValue = b[key];

    if (key === "unit_type") {
      aValue = a["unit_bedroom"] || 0;
      bValue = b["unit_bedroom"] || 0;

      return order === "asc" ? aValue - bValue : bValue - aValue;
    }

    if (key === "unit_rent_min") {
      const extractPrice = (priceStr) => {
        return parseFloat(priceStr);
      };

      aValue = extractPrice(aValue);
      bValue = extractPrice(bValue);

      return order === "asc" ? aValue - bValue : bValue - aValue;
    }

    if (key === "availability_date") {
      const now = new Date();

      const parseDate = (dateStr) => {
        if (!dateStr || dateStr === "Inquire for Details") {
          return null;
        }

        if (dateStr === "Now") {
          return now;
        }

        return new Date(dateStr);
      };

      aValue = parseDate(aValue);
      bValue = parseDate(bValue);

      if (!aValue && !bValue) {
        return 0;
      }

      if (!aValue) {
        return order === "asc" ? 1 : -1;
      }

      if (!bValue) {
        return order === "asc" ? -1 : 1;
      }

      return order === "asc" ? aValue - bValue : bValue - aValue;
    }

    if (key === "area" || (typeof aValue === "number" && typeof bValue === "number")) {
      aValue = Number(aValue);
      bValue = Number(bValue);

      return order === "asc" ? aValue - bValue : bValue - aValue;
    }

    if (typeof aValue === "string" && typeof bValue === "string") {
      return order === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    }

    return 0;
  });
}

export function findNearestAvailability(data) {
  if (!data || data.length === 0) {
    return null;
  }

  const today = moment();
  let nearestDate = null;

  let inquireForDetailsPresent = false;

  for (const unit of data) {
    const availabilityDate = unit.availability_date;

    if (availabilityDate === "Now") {
      return "Now";
    } else if (availabilityDate === "Inquire for Details") {
      inquireForDetailsPresent = true;
    } else {
      const parsedDate = moment(availabilityDate, "MM/DD/YYYY");

      if (parsedDate.isValid() && parsedDate.isAfter(today)) {
        if (!nearestDate || parsedDate.isBefore(nearestDate)) {
          nearestDate = parsedDate.format("MMM D, YYYY");
        }
      }
    }
  }

  if (nearestDate) {
    return nearestDate;
  } else if (inquireForDetailsPresent) {
    return "Inquire for Details";
  }

  return null;
}

export function isValidDateRange(input) {
  if (typeof input !== "string") {
    return false;
  }

  const dateFormat = /^\d{4}-\d{2}-\d{2}$/;

  const dates = input.split(",");

  if (dates.length !== 2) {
    return false;
  }

  const [date1, date2] = dates.map((date) => date.trim());

  if (!dateFormat.test(date1) || !dateFormat.test(date2)) {
    return false;
  }

  const d1 = new Date(date1);
  const d2 = new Date(date2);

  return d1 <= d2;
}

export const formatUnitData = (data) => {
  const formatCurrency = (value) => (value && !isNaN(value) ? `$${new Intl.NumberFormat().format(value)}` : "$- -");
  const formatNumber = (value) => (!isNaN(value) ? new Intl.NumberFormat().format(value) : "- -");
  const formatArea = (value) => (!isNaN(value) ? `${new Intl.NumberFormat().format(value)} Sq.ft` : "- -");

  const formatAvailabilityDate = (date) => {
    if (!date) {
      return "- -";
    }

    return date === "Now" ? "Now" : date === "Inquire for Details" ? "Inquire for Details" : getFormattedDate(getDateString(date).replace("Available", ""));
  };

  const formatUnitType = (value) => {
    if (!value || typeof value !== "string") {
      return "- -";
    }

    return UNIT_TYPE_LABELS[value] ?? translation(value, "- -");
  };

  return {
    name: data?.unit_name || "- -",
    fp_name: data?.fp_name || "- -",
    area: formatNumber(data.area) || "- -",
    areaFt: formatArea(data.area) || "- -",
    unitType: formatUnitType(data?.unit_type) || "- -",
    unitRentMin: formatCurrency(data.unit_rent_min) || "- -",
    availabilityDate: formatAvailabilityDate(data.availability_date) || "- -",
    unitBathroom: !isNaN(data.unit_bathroom) ? `${formatNumber(data.unit_bathroom)} ${t("bath")}` : "- -",
  };
};
